/**
 * Patriot tools — support-only views over the Patriot replica. The pages
 * themselves live in the app; this is the data they read and the rules for
 * reading it.
 */
export {
  useTenantActivations,
  useTenantActivation,
  useFleetActivations,
  useFleetActivation,
  useTenantSignals,
  ACTIVATIONS_PAGE_SIZE,
  SIGNALS_PAGE_SIZE,
  type UseTenantActivationsOptions,
  type UseFleetActivationsOptions,
  type UseActivationsResult,
  type UseFleetListResult,
  type UseActivationResult,
  type UseTenantSignalsOptions,
  type UseSignalsResult,
} from "./hooks";
export {
  useFleetClients,
  useFleetClient,
  useFleetClientVersions,
  parseClientVersionParam,
  CLIENTS_PAGE_SIZE,
  CLIENT_VERSIONS_PAGE_SIZE,
  LATEST_CLIENT_VERSION,
  type ClientVersionParam,
  type FleetClientFilters,
  type UseClientsResult,
  type UseClientResult,
  type UseClientVersionsResult,
} from "./clients";
export type {
  ClientAddress,
  ClientContact,
  ClientDetail,
  ClientSummary,
  ClientVersionSummary,
} from "../../client-messages/clients";
export {
  useFleetOperatorNotes,
  OPERATOR_NOTES_PAGE_SIZE,
  type FleetOperatorNoteFilters,
  type UseFleetOperatorNotesOptions,
  type UseOperatorNotesResult,
} from "./operator-notes";
export type { FleetOperatorNoteSummary } from "../../client-messages/operator-notes";
export {
  useFleetSignals,
  useFleetSignal,
  useTenantSignal,
  type FleetSignalFilters,
  type UseSignalResult,
  type UseFleetSignalsOptions,
  type UseFleetSignalsResult,
} from "./fleet-signals";
export { parseTenantSignalFilters, type TenantSignalFilters } from "./signal-filters";
export {
  parseTenantActivationFilters,
  parseFleetActivationFilters,
  FLEET_DEFAULT_DAYS,
  type TenantActivationFilters,
  type FleetActivationFilters,
  type ParsedFleetActivationFilters,
  type ParsedTenantActivationFilters,
  type RawActivationFilters,
} from "./activation-filters";
export {
  useTenantIncidents,
  useFleetIncidents,
  useTenantIncident,
  useFleetIncident,
  useTenantIncidentSummary,
  useFleetIncidentSummary,
  INCIDENTS_PAGE_SIZE,
  type UseIncidentsResult,
  type UseFleetIncidentsOptions,
  type UseIncidentResult,
  type UseIncidentSummaryResult,
} from "./incidents";
export {
  parseIncidentFilters,
  incidentPeriodDays,
  incidentPeriodRange,
  INCIDENT_DEFAULT_DAYS,
  type IncidentFilters,
  type FleetIncidentFilters,
  type ParsedIncidentFilters,
} from "./incident-filters";
export {
  callFacts,
  calledOn,
  callTitle,
  describeIncidentTimeline,
  incidentTimelineSpan,
  type DescribedIncidentStep,
  type IncidentTimelineRail,
  type IncidentTimelineRow,
  type IncidentTimelineSpan,
} from "./incident-steps";
export type {
  IncidentActivation,
  IncidentCall,
  IncidentDetail,
  IncidentPeriodSummary,
  IncidentStep,
  IncidentSummary,
  IncidentSummaryPeriod,
  IncidentSummaryResponse,
} from "../../client-messages/incidents";
export { isTimeZone } from "./wall-clock";
export { addDays, localEndBound, localStartBound, offsetLabel, timezoneAbbreviation } from "@visioni/a3s-dates";
export {
  defaultTimeZone,
  matchesTimeZone,
  timeZoneAfterSiteChange,
  timeZoneLabel,
  timeZoneOptions,
  type DefaultTimeZone,
  type TimeZoneGroup,
  type TimeZoneOption,
} from "./time-zone-choice";
export { activationCameraKey, chronological, formatElapsed } from "./activation-view";
export type { CursorPager } from "./cursor-pager";
export type {
  ActivationDetail,
  ActivationOperatorNote,
  ActivationSignal,
  ActivationSignalProcessing,
  ActivationSummary,
  FleetActivationDetail,
  FleetActivationSummary,
} from "../../client-messages/activations";
export type { FleetSignalDetail, FleetSignalSummary, SignalDetail, SignalSummary } from "../../client-messages/signals";
export type { ListOrder } from "../../client-messages/activations";
export { useRunPatriotConnectionCheck } from "./connection-check";
export type {
  PatriotConnectionCheckStepResult,
  RunPatriotConnectionCheckResponse,
} from "../../client-messages/patriot-connection-check";
export {
  useStartPatriotBackfill,
  useControlPatriotBackfill,
  useBackfillRuns,
  BACKFILL_RUNS_PAGE_SIZE,
  type UseBackfillRunsOptions,
  type UseBackfillRunsResult,
} from "./backfill";
export {
  PATRIOT_BACKFILL_LANES,
  UNDATED_PATRIOT_BACKFILL_LANES,
  type PatriotBackfillControl,
  type PatriotBackfillLane,
  type PatriotBackfillRun,
  type PatriotBackfillScan,
  type PatriotBackfillScanName,
  type StartPatriotBackfillRequest,
  type StartPatriotBackfillResponse,
} from "../../client-messages/patriot-backfill";
export {
  useStartIncidentsRecompute,
  useIncidentsRecomputes,
  INCIDENTS_RECOMPUTES_PAGE_SIZE,
  type UseIncidentsRecomputesResult,
} from "./incidents-recompute";
export { incidentsRecomputePeriod } from "./incidents-recompute-period";
export type {
  IncidentsRecomputeChunk,
  IncidentsRecomputeRun,
  StartIncidentsRecomputeRequest,
  StartIncidentsRecomputeResponse,
} from "../../client-messages/incidents-recompute";
